import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';

// Logo URLs
const LOGO_BLACK_KEY = '/logo-black.png';
const LOGO_WHITE_KEY = '/logo-white.png';

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
  };

  const logo = theme === 'dark' ? LOGO_WHITE_KEY : LOGO_BLACK_KEY;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(identifier, password);
      // Everyone except requesters goes to dashboard (Kanban)
      const home = user.role === 'requester' ? '/my-maintenance' : '/dashboard';
      navigate(home);
    } catch {
      toast.error(t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 transition-colors" data-testid="login-page">
      {/* Top controls */}
      <div className="absolute top-4 end-4 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          className="text-muted-foreground hover:text-foreground"
          data-testid="login-theme-toggle"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          data-testid="login-language-toggle"
        >
          <Globe className="w-4 h-4" />
          <span className="font-medium">{i18n.language === 'ar' ? 'EN' : 'ع'}</span>
        </Button>
      </div>

      <Card className="w-full max-w-md shadow-xl border bg-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <img key={theme} src={logo} alt="Logo" className="w-20 h-20 object-contain mx-auto" />
          </div>
          <CardTitle className="font-barlow text-2xl font-bold text-foreground tracking-tight">
            {t('common.appName')}
          </CardTitle>
          <CardDescription className="text-muted-foreground">{t('common.appSubtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-foreground">{t('auth.emailOrUsername')}</Label>
              <Input
                id="identifier"
                type="text"
                placeholder={t('auth.emailOrUsernamePlaceholder')}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="bg-input text-foreground border-border placeholder:text-muted-foreground"
                data-testid="login-identifier-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-input text-foreground border-border placeholder:text-muted-foreground"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-brand hover:bg-brand-hover text-brand-foreground"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="absolute bottom-14 sm:bottom-4 start-4 text-[11px] text-muted-foreground/50 select-none" data-testid="login-footer-text">
        {i18n.language === 'ar' ? 'مبني للصيانة اليومية في أنون' : 'Built for Unknownroom daily maintenance'}
      </p>
    </div>
  );
}
